import { createSelector } from 'reselect';

import { objectKeys } from '@iconik-io/core/src/object_entries';
import type { MetadataMetadataViewSchema } from '@iconik-io/iconik-api/src/services/metadata';
import type { AssetEntityNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/asset';

import type { TimebasedMetadataFilters } from '../../ducks/asset_time_based_metadata/types';
import { selectCurrentVersionId } from '../../ducks/version/selectors';
import type { AppState } from '../../store/types';

const getAsset = (
  state: AppState,
  assetId: string | undefined,
): AssetEntityNormalized | undefined =>
  assetId ? state.entities.assets[assetId] : undefined;

const getSegments = (state: AppState) => state.entities.segments;

const getAssetTimeBasedMetadataIds = (
  state: AppState,
  assetId: string | undefined,
): string[] =>
  assetId ? state.assetTimeBasedMetadataReducer.segmentIds[assetId] : [];

const getTimebasedMetadataFilteredIds = (
  state: AppState,
  assetId: string | undefined,
): string[] =>
  assetId
    ? state.assetTimeBasedMetadataReducer.filteredSegmentsIds[assetId]
    : [];

export const getTimebasedMetadataFilters = (
  state: AppState,
): TimebasedMetadataFilters => state.assetTimeBasedMetadataReducer.filters;

const getAssetCurrentVersionId = (
  state: AppState,
  assetId: string | undefined,
  versionId: string | undefined,
): string | undefined | null =>
  selectCurrentVersionId(state, assetId, versionId);

const getViews = (state: AppState) => state.entities.metadata_views;

export const selectSegmentIds = createSelector(
  [
    getAsset,
    getSegments,
    getAssetTimeBasedMetadataIds,
    getTimebasedMetadataFilteredIds,
    getTimebasedMetadataFilters,
    getAssetCurrentVersionId,
    getViews,
  ],
  (
    asset,
    segments,
    timeBasedMetadataIds,
    searchedIds,
    filters,
    currentVersionId,
    views,
  ) => {
    let resultIds = searchedIds ?? timeBasedMetadataIds ?? [];
    resultIds = resultIds.filter((id) => {
      const segment = segments[id];
      if (!segment) {
        return false;
      }
      if (segment.metadata_view_id && !views[segment.metadata_view_id]) {
        return false;
      }
      return segment.status !== 'DELETED';
    });
    if (filters && Object.keys(filters).length) {
      objectKeys(filters).forEach((filterName) => {
        const filter = filters[filterName];

        if (Array.isArray(filter) && filter.length) {
          resultIds = resultIds.filter((id) => {
            if (filterName === 'view_id') {
              const viewId = segments[id].metadata_view_id;
              if (!viewId) {
                return false;
              }
              return filters[filterName]?.includes(viewId);
            }
            const filterValue = segments[id]?.[filterName];
            if (!filterValue) {
              return false;
            }
            return filter.includes(filterValue);
          });
        }
      });
    }

    const versions = asset?.versions ?? [];
    if (currentVersionId && versions.length > 1) {
      resultIds = resultIds.filter(
        (id) => segments[id].version_id === currentVersionId,
      );
    }

    return resultIds;
  },
);

const getMetadataCategoryViews = (state: AppState) =>
  state.metadataCategories.metadataCategoryViews;

export const selectTagMetadataView = createSelector(
  getMetadataCategoryViews,
  (metadataCategoryViews) => {
    let tagMetadataView: MetadataMetadataViewSchema | undefined;
    const { segments } = metadataCategoryViews;
    if (segments) {
      const tagViews = segments.tag?.views ?? [];
      tagMetadataView = tagViews.find((view) => view.name === 'Segment Tags');
    }
    return tagMetadataView;
  },
);
